import React from 'react';
import { Link } from '@/components/LocaleLink';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import GenAIStrategySection from '@/components/solutions/GenAIStrategySection';
import WhyRetailDataFailsSection from '@/components/WhyRetailDataFailsSection';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { rasCopy } from '@/contexts/language/translations/pages/ras';

/**
 * Hero: cloud.png (oval), ras_screen.png (laptop). Rest of images: image-2.png … image-8.png (see SOLUTIONS_IMAGES.md).
 */

const Solutions = () => {
  const { language } = useLanguage();
  const c = rasCopy[language];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title={c.seoTitle}
        description={c.seoDescription}
        canonical="/solutions/retail-automation-suite"
      />
      <Navigation />
      <main className="index-page px-7 md:px-10 flex-1">
        {/* Hero: soft blurred glow (same pair used on the Data Warehousing hero), text inside, ras_screen overlaps bottom of it */}
        <section className="relative pt-24 pb-8 md:pt-28 md:pb-12 overflow-visible bg-white">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-24 -left-24 w-[560px] h-[560px] bg-[#ed6952] rounded-full filter blur-3xl opacity-20" />
          </div>
          {/* Cloud – bigger area so heading fits; ras_screen will overlap this from below */}
          <div
            className="relative mx-auto flex flex-col items-center justify-start text-center min-h-[560px] md:min-h-[860px] px-6 pt-6 md:pt-10 pb-6"
            style={{
              maxWidth: 'min(96vw, 1320px)',
              width: '100%',
            }}
          >
            <div className="max-w-4xl mx-auto">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-5">{c.hero.eyebrow}</p>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#2A1E47] mb-6 leading-[1.08]">
                {c.hero.title}
              </h1>
              <p className="text-lg md:text-xl text-[#2A1E47]/80 max-w-2xl mx-auto mb-3">
                {c.hero.tagline}
              </p>
              <p className="text-base text-muted-foreground max-w-xl mx-auto mb-10">
                {c.hero.subtitle}
              </p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <Link
                  to="/book-a-call"
                  className="inline-flex items-center justify-center px-8 py-3 rounded-lg bg-[#FF6B35] text-white font-medium hover:bg-[#FF6B35]/90 transition-colors"
                >
                  {c.hero.cta}
                </Link>
              </div>
            </div>
          </div>
          {/* Laptop – pulled up into cloud, just a bit below Contact us button */}
          <div className="relative z-10 flex justify-center -mt-[110px] md:-mt-[380px] px-0">
            <img
              src="/lovable-uploads/ras_screen.png"
              alt={c.hero.imageAlt}
              className="w-full max-w-4xl md:max-w-5xl h-auto object-contain drop-shadow-lg"
            />
          </div>
        </section>

        {/* Why Traditional Retail Data Fails – shared content, see WhyRetailDataFailsSection.tsx */}
        <section id="challenge" className="pt-8 md:pt-12 pb-16 md:pb-24 bg-white">
          <div className="text-center mb-8">
            <span
              className="inline-block px-5 py-2.5 rounded-lg text-white text-sm font-medium"
              style={{ backgroundColor: '#2A1E48' }}
            >
              {c.challengeBadge}
            </span>
          </div>
          <WhyRetailDataFailsSection variant="gradient" trackingLabel="ras_why_retail_data_fails_section" />
        </section>

        {/* RAS – The Unified Approach: Solution button, 3 blocks with text + image/visuals – no top padding (96px from Challenge section below is enough) */}
        <section className="pt-0 pb-16 md:pb-24 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-6">
              <span
                className="inline-block px-5 py-2.5 rounded-lg text-white text-sm font-medium"
                style={{ backgroundColor: '#2A1E48' }}
              >
                {c.solution.badge}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
              {c.solution.title}
            </h2>
            <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-16">
              {c.solution.subtitle}
            </p>

            {/* Block 1: Unified Retail Data Model – text left, logos grid right */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center mb-20">
              <div>
                <h3 className="text-2xl font-bold text-foreground mb-4">{c.dataModel.title}</h3>
                <p className="text-muted-foreground mb-4">
                  {c.dataModel.p1}
                </p>
                <p className="text-muted-foreground mb-4">
                  {c.dataModel.p2}
                </p>
                <p className="text-muted-foreground">
                  {c.dataModel.p3}
                </p>
              </div>
              {/* Yellow box 460×275, yellow top+left; blue min 400px, logos centered; Excel icon only smaller inside same circle */}
              <div className="rounded-[24px] pt-5 pl-5 flex justify-end items-end w-full max-w-[460px] min-h-[275px]" style={{ backgroundColor: '#FFF7EC' }}>
                <div className="rounded-[20px] p-5 grid grid-cols-3 gap-6 w-full max-w-[400px] md:min-w-[400px] md:w-[400px] place-content-center place-items-center" style={{ backgroundColor: '#E0E7FF' }}>
                  {[
                    { file: 'ga4_icon.png', alt: 'GA4' },
                    { file: 'sap_icon.png', alt: 'SAP' },
                    { file: 'sheets_icon.png', alt: 'Google Sheets' },
                    { file: 'shopify_icon.png', alt: 'Shopify' },
                    { file: 'ads_icon.png', alt: 'Google Ads' },
                    { file: 'abra_icon.png', alt: 'Abra' },
                    { file: 'magento_icon.png', alt: 'Magento' },
                    { file: 'shoptet_icon.png', alt: 'Shoptet' },
                    { file: 'excel_icon.png', alt: 'Excel', small: true },
                  ].map((logo) => (
                    <div key={logo.alt} className="rounded-full w-12 h-12 flex items-center justify-center bg-white shadow-sm flex-shrink-0 p-1.5">
                      <img
                        src={`/lovable-uploads/${logo.file}`}
                        alt={logo.alt}
                        className={`w-full h-full object-contain ${logo.small ? 'max-w-[70%] max-h-[70%]' : ''}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Block 2: yellow 460×275, yellow top+right, image bottom-left */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center mb-20">
              <div className="order-2 md:order-1 rounded-[24px] pt-5 pr-5 pl-0 pb-0 flex justify-start items-end w-full max-w-[460px] min-h-[275px]" style={{ backgroundColor: '#FFF7EC' }}>
                <div className="flex-1 min-w-0 flex justify-start items-end">
                  <img
                  src="/lovable-uploads/strategic-north-star-tracking.png"
                  alt={c.northStar.imageAlt}
                  className="w-full h-auto object-contain object-bottom rounded-2xl max-h-[245px]"
                  />
                </div>
              </div>
              <div className="order-1 md:order-2">
                <h3 className="text-2xl font-bold text-foreground mb-4">{c.northStar.title}</h3>
                <p className="text-muted-foreground mb-4">
                  {c.northStar.p1}
                </p>
                <p className="text-muted-foreground mb-4">
                  {c.northStar.p2}
                </p>
                <p className="text-muted-foreground">
                  {c.northStar.p3}
                </p>
              </div>
            </div>

            {/* Block 3: Off-the-Shelf Automation – list left, image right */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center mb-20">
              <div>
                <h3 className="text-2xl font-bold text-foreground mb-4">{c.automation.title}</h3>
                <ul className="space-y-3 text-muted-foreground">
                  {c.automation.items.map((item, i) => {
                    // "Name – description" (sk) / "Name: description" (en): name on its own line, description below
                    const [name, ...rest] = item.split(/ – |: /);
                    const description = rest.join(': ');
                    return (
                      <li key={i} className="flex gap-3">
                        <span className="flex-shrink-0 w-2 h-2 rounded-full mt-2 bg-[#FFECD3]" style={{ minWidth: 8, minHeight: 8 }} aria-hidden />
                        {description ? (
                          <span>
                            <span className="block font-semibold text-foreground">{name}</span>
                            <span className="block">{description}</span>
                          </span>
                        ) : (
                          <span>{item}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
              {/* Yellow box 460×275, yellow top+left, image bottom-right + rounded */}
              <div className="rounded-[24px] pt-5 pl-5 pr-0 pb-0 flex justify-end items-end w-full max-w-[460px] min-h-[275px]" style={{ backgroundColor: '#FFF7EC' }}>
                <div className="flex-1 min-w-0 flex justify-end items-end">
                  <img
                    src="/lovable-uploads/off-the-shelf%20automation.png"
                    alt={c.automation.imageAlt}
                    className="w-auto max-w-full h-auto object-contain object-bottom object-right rounded-2xl max-h-[245px]"
                  />
                </div>
              </div>
            </div>

            {/* Block 4: Retail context engineered dataset for AI – box left, text right */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
              <div className="order-2 md:order-1 rounded-[24px] pt-5 pr-5 pl-0 pb-0 flex justify-start items-end w-full max-w-[460px] min-h-[275px]" style={{ backgroundColor: '#FFF7EC' }}>
                <div className="flex-1 min-w-0 flex justify-start items-end">
                  <img
                    src="/lovable-uploads/retail-ai-dataset.png"
                    alt={c.aiDataset.imageAlt}
                    className="w-full h-auto object-contain object-bottom rounded-2xl max-h-[245px]"
                  />
                </div>
              </div>
              <div className="order-1 md:order-2">
                <h3 className="text-2xl font-bold text-foreground mb-4">{c.aiDataset.title}</h3>
                <ul className="space-y-3 text-muted-foreground">
                  {c.aiDataset.items.map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="flex-shrink-0 w-2 h-2 rounded-full mt-2 bg-[#FFECD3]" style={{ minWidth: 8, minHeight: 8 }} aria-hidden />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Rovnaká veľkosť ako na hlavnej stránke (py-20); text + button posunuté nižšie (väčšia medzera hore, menšia dole) */}
        <section className="relative overflow-hidden py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35] via-[#FF7F27] to-[#FF4500] rounded-3xl" />
          <div className="container-custom relative z-10">
            <div className="max-w-3xl mx-auto text-center pt-16 pb-8 px-4">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white leading-tight">
                {c.cta.title}
              </h2>
              <p className="text-lg md:text-xl text-white mb-10 max-w-xl mx-auto">
                {c.cta.text}
              </p>
              <div className="flex justify-center">
                <Link
                  to="/tell-me-more"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-[#2a1e48] hover:bg-[#2a1e48]/90 text-white text-base font-medium transition-all duration-300 shadow-lg border border-white/10"
                >
                  {c.cta.button}
                </Link>
              </div>
            </div>
          </div>
        </section>

        <GenAIStrategySection />
      </main>
      <Footer />
    </div>
  );
};

export default Solutions;
